import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import PartnerService from "services/PartnerService";

const initialState = {
  partners: [],
  partner: null,
  loading: false,
  detailLoading: false,
  error: null,
  pagination: {
    total: 0,
    currentPage: 1,
    lastPage: 1,
  },
};

export const fetchPartners = createAsyncThunk(
  "partners/fetchPartners",
  async (
    { page = 1, limit = 10, search = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerService.getPartners(page, limit, search);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "Erreur lors de la récupération des partenaires"
      );
    }
  }
);

export const fetchPartnerDetail = createAsyncThunk(
  "partners/fetchPartnerDetail",
  async (partnerId, { rejectWithValue }) => {
    try {
      const response = await PartnerService.getPartner(partnerId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "Erreur lors de la récupération du partenaire"
      );
    }
  }
);

export const updatePartner = createAsyncThunk(
  "partners/updatePartner",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await PartnerService.updatePartner(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "Erreur pendant la mise à jour du partenaire"
      );
    }
  }
);

export const updatePartnerStatus = createAsyncThunk(
  "partners/updatePartnerStatus",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await PartnerService.updatePartnerStatus(id, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "Erreur lors de la mise à jour du statut"
      );
    }
  }
);

export const confirmPartnerPayment = createAsyncThunk(
  "partners/confirmPartnerPayment",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await PartnerService.confirmPartnerPayment(id, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "Erreur lors de la confirmation du paiement"
      );
    }
  }
);

export const removePartner = createAsyncThunk(
  "partners/removePartner",
  async (id, { rejectWithValue }) => {
    try {
      await PartnerService.deletePartner(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "Erreur lors de la suppression du partenaire"
      );
    }
  }
);

export const createPartner = createAsyncThunk(
  "partners/createPartner",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await PartnerService.createPartner(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          "Erreur lors de la création du partenaire"
      );
    }
  }
);

const partnerSlice = createSlice({
  name: "partners",
  initialState,
  reducers: {
    clearPartnerDetail: (state) => {
      state.partner = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPartners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartners.fulfilled, (state, action) => {
        state.loading = false;
        state.partners = action.payload?.data || [];
        state.pagination = {
          total: action.payload?.total || 0,
          currentPage: action.payload?.currentPage || 1,
          lastPage: action.payload?.lastPage || 1,
        };
      })
      .addCase(fetchPartners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchPartnerDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchPartnerDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.partner = action.payload;
      })
      .addCase(fetchPartnerDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updatePartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePartner.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPartner = action.payload;
        state.partners = state.partners.map((partner) =>
          partner.id === updatedPartner.id ? { ...partner, ...updatedPartner } : partner
        );
        if (state.partner?.id === updatedPartner.id) {
          state.partner = updatedPartner;
        }
      })
      .addCase(updatePartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updatePartnerStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePartnerStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPartner = action.payload;
        state.partners = state.partners.map((partner) =>
          partner.id === updatedPartner.id ? { ...partner, ...updatedPartner } : partner
        );
        if (state.partner?.id === updatedPartner.id) {
          state.partner = updatedPartner;
        }
      })
      .addCase(updatePartnerStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(confirmPartnerPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmPartnerPayment.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(confirmPartnerPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(removePartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removePartner.fulfilled, (state, action) => {
        state.loading = false;
        state.partners = state.partners.filter(
          (partner) => partner.id !== action.payload
        );
        if (state.partner?.id === action.payload) {
          state.partner = null;
        }
      })
      .addCase(removePartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(createPartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPartner.fulfilled, (state, action) => {
        state.loading = false;
        state.partners = [action.payload, ...state.partners];
      })
      .addCase(createPartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearPartnerDetail } = partnerSlice.actions;
export default partnerSlice.reducer;
